// Customer Story — article cards (Figma 4287:45898 desktop / 5616:19429 mobile).
// Each narrative section is a white card with a small grey label, a heading and
// markdown body. On mobile the cards are full-bleed and self-bordered; on
// desktop the column supplies the side borders, so each card only divides.

import { RichText } from "@/components/rich-text";
import type { CaseStudyStory } from "@/lib/content/schemas";

import { CustomerStoryStatValue } from "./stat-value";

interface CustomerStorySectionProps {
  /** Anchor id used by the scroll-nav. */
  id: string;
  /**
   * Small grey category (e.g. "Challenge") — mirrors the scroll-nav item.
   * Stories written as plain `##` headings have no category, so the nav falls
   * back to the heading; the eyebrow is dropped rather than repeating it.
   */
  label: string;
  title: string;
  /** Markdown body. */
  body: string;
}

export function CustomerStorySection({
  id,
  label,
  title,
  body,
}: CustomerStorySectionProps) {
  return (
    <section
      id={id}
      className="border-grey-100 bg-grey-0 flex scroll-mt-[148px] flex-col gap-5 border px-6 py-8 lg:scroll-mt-24 lg:gap-8 lg:border-x-0 lg:border-t-0 lg:border-b lg:p-16"
    >
      <div className="flex flex-col gap-3">
        {label === title ? null : (
          <p className="text-grey-500 text-[12px] leading-[1.4] tracking-[-0.24px]">
            {label}
          </p>
        )}
        <h2 className="text-ds-heading-h3 text-grey-950">{title}</h2>
      </div>
      {/* Body-copy sizing targets this card's own paragraphs only — a nested
          `[&_p]` would also shrink the paragraph inside a pull quote. */}
      <div className="text-grey-600 flex flex-col gap-4 [&_a]:underline [&>p]:text-[14px] [&>p]:leading-[1.5] [&>p]:tracking-[-0.28px] md:[&>p]:text-[16px] md:[&>p]:tracking-[-0.32px]">
        <RichText value={body} />
      </div>
    </section>
  );
}

interface CustomerStoryStatsBandProps {
  stats: NonNullable<CaseStudyStory["stats"]>;
}

export function CustomerStoryStatsBand({ stats }: CustomerStoryStatsBandProps) {
  return (
    <div className="border-grey-100 bg-grey-0 flex flex-col gap-5 border px-6 py-8 lg:flex-row lg:items-start lg:gap-6 lg:border-x-0 lg:border-t-0 lg:border-b lg:p-16">
      {stats.map((stat) => (
        <div key={stat.label} className="flex flex-1 flex-col gap-2">
          <CustomerStoryStatValue
            value={stat.value}
            hoverGradient
            className="text-ds-heading-h2"
          />
          <p className="text-grey-600 text-[14px] leading-[1.4] tracking-[-0.56px]">
            {stat.label}
          </p>
        </div>
      ))}
    </div>
  );
}
